import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Shield, 
  Users, 
  LogOut,
  LayoutDashboard,
  ShoppingBag,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Edit,
  Building2,
  User,
  CheckCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

const AdminRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [serviceType, setServiceType] = useState("all");
  const [skip, setSkip] = useState(0);
  const [editRequest, setEditRequest] = useState(null);
  const [editForm, setEditForm] = useState({ status: "", quote: "", admin_notes: "" });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const limit = 20;

  const fetchRequests = useCallback(async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: skip.toString(), limit: limit.toString() });
      if (status !== "all") params.append("status", status);
      if (serviceType !== "all") params.append("service_type", serviceType);
      
      const response = await axios.get(`${API}/admin/service-requests?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data.requests);
      setTotal(response.data.total);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/admin/login");
      } else {
        toast.error("Failed to load requests");
      }
    } finally {
      setLoading(false);
    }
  }, [skip, status, serviceType, navigate]);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchRequests();
  }, [fetchRequests, navigate]);

  const handleEditRequest = (req) => {
    setEditRequest(req);
    setEditForm({
      status: req.status,
      quote: req.quote || "",
      admin_notes: req.admin_notes || ""
    });
  };

  const handleSaveRequest = async () => {
    setSaving(true);
    try {
      const payload = {
        status: editForm.status,
        admin_notes: editForm.admin_notes
      };
      if (editForm.quote) {
        payload.quote = parseFloat(editForm.quote);
      }
      
      await axios.put(`${API}/admin/service-requests/${editRequest.request_id}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` }
      });
      toast.success("Request updated successfully");
      setEditRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error("Failed to update request");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminData");
    navigate("/admin/login");
  };

  const handleConfirmPayment = async (requestId, installmentIndex) => {
    try {
      await axios.post(
        `${API}/admin/service-requests/${requestId}/confirm-payment?installment_index=${installmentIndex}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` } }
      );
      toast.success("Payment confirmed");
      fetchRequests();
    } catch (error) {
      toast.error("Failed to confirm payment");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed": return "bg-emerald-500/10 text-emerald-400 border-emerald-500/30";
      case "in_progress": return "bg-blue-500/10 text-blue-400 border-blue-500/30";
      case "quoted": return "bg-violet-500/10 text-violet-400 border-violet-500/30";
      case "accepted": return "bg-primary/10 text-primary border-primary/30";
      case "paid": return "bg-emerald-500/10 text-emerald-400 border-emerald-500/30";
      case "declined": return "bg-red-500/10 text-red-400 border-red-500/30";
      default: return "bg-amber-500/10 text-amber-400 border-amber-500/30";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "Not quoted";
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Admin Navbar */}
      <nav className="bg-slate-900/80 backdrop-blur-xl border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-violet-400" />
              </div>
              <span className="text-lg font-bold text-white">Admin Portal</span>
            </div>
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              className="text-slate-400 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Link to="/admin/dashboard">
            <Button variant="outline" className="btn-secondary">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </Link>
          <Link to="/admin/users">
            <Button variant="outline" className="btn-secondary">
              <Users className="w-4 h-4 mr-2" />
              Users
            </Button>
          </Link>
          <Link to="/admin/requests">
            <Button className="bg-violet-600 hover:bg-violet-700 text-white">
              <ShoppingBag className="w-4 h-4 mr-2" />
              Service Requests
            </Button>
          </Link>
          <Link to="/admin/transactions">
            <Button variant="outline" className="btn-secondary">
              <CreditCard className="w-4 h-4 mr-2" />
              Transactions
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <Card className="card-dashboard mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <Select value={status} onValueChange={(v) => { setStatus(v); setSkip(0); }}>
                <SelectTrigger className="w-full sm:w-44 input-default">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="quoted">Quoted</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={serviceType} onValueChange={(v) => { setServiceType(v); setSkip(0); }}>
                <SelectTrigger className="w-full sm:w-52 input-default" data-testid="filter-service-type-select">
                  <SelectValue placeholder="Service Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  <SelectItem value="property_management">Property Management</SelectItem>
                  <SelectItem value="professional_valuation">Professional Valuation</SelectItem>
                  <SelectItem value="documentation_processing">Documentation Processing</SelectItem>
                  <SelectItem value="leasing_sourcing">Leasing & Sourcing</SelectItem>
                  <SelectItem value="development_advisory">Development Advisory</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Requests List */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-amber-500" />
              Service Requests ({total})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingBag className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No service requests found</p>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {requests.map((req) => (
                    <div 
                      key={req.request_id}
                      className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-slate-600 transition-colors"
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-white font-medium capitalize">
                              {req.service_type.replace(/_/g, ' ')}
                            </h3>
                            <Badge className={`capitalize ${getStatusBadge(req.status)}`}>
                              {req.status.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          
                          <div className="grid sm:grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center gap-2 text-slate-400">
                              <User className="w-4 h-4" />
                              <span>{req.user?.first_name} {req.user?.last_name}</span>
                              <span className="text-slate-500">({req.user?.email})</span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-400">
                              <Building2 className="w-4 h-4" />
                              <span>{req.asset?.name || 'Unknown'}</span>
                              <span className="text-slate-500">({req.asset?.state})</span>
                            </div>
                          </div>
                          
                          {req.description && (
                            <p className="text-slate-400 text-sm mt-2">{req.description}</p>
                          )}
                          
                          <div className="flex items-center gap-4 mt-3 text-sm">
                            <span className="text-slate-500">
                              Submitted: {new Date(req.created_at).toLocaleDateString()}
                            </span>
                            {req.quote && (
                              <span className="text-emerald-400 font-medium mono">
                                Quote: {formatCurrency(req.quote)}
                              </span>
                            )}
                            {req.payment_method && (
                              <Badge className="bg-slate-700/50 text-slate-300 border-slate-600 capitalize">
                                {req.payment_method === "bank_transfer" ? "Bank Transfer" : "Paystack"}
                              </Badge>
                            )}
                          </div>
                          
                          {/* Invoice installments with confirm button for bank transfers */}
                          {req.invoice && req.payment_method === "bank_transfer" && (
                            <div className="mt-3 pt-3 border-t border-slate-700 space-y-2">
                              <p className="text-slate-400 text-xs font-medium">Invoice {req.invoice.invoice_id}</p>
                              {req.invoice.installments.map((inst, idx) => (
                                <div key={`installment-${req.request_id}-${idx}`} className="flex items-center justify-between text-sm p-2 rounded-lg bg-slate-800/50">
                                  <div>
                                    <span className="text-slate-300">{inst.label}</span>
                                    <span className="text-white mono ml-2">{formatCurrency(inst.amount)}</span>
                                  </div>
                                  {inst.status === "paid" ? (
                                    <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                                      <CheckCircle className="w-3 h-3 mr-1" /> Confirmed
                                    </Badge>
                                  ) : (
                                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                      onClick={() => handleConfirmPayment(req.request_id, idx)}
                                      data-testid={`confirm-payment-${req.request_id}-${idx}`}>
                                      <CheckCircle className="w-3 h-3 mr-1" /> Confirm Payment
                                    </Button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditRequest(req)}
                          className="btn-secondary"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Update
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800">
                  <p className="text-slate-400 text-sm">
                    Showing {skip + 1} - {Math.min(skip + limit, total)} of {total}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSkip(Math.max(0, skip - limit))}
                      disabled={skip === 0}
                      className="btn-secondary"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSkip(skip + limit)}
                      disabled={skip + limit >= total}
                      className="btn-secondary"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Edit Request Modal */}
        <Dialog open={!!editRequest} onOpenChange={() => setEditRequest(null)}>
          <DialogContent className="bg-slate-900 border-slate-800">
            <DialogHeader>
              <DialogTitle className="text-white">Update Service Request</DialogTitle>
              <DialogDescription className="text-slate-400">
                {editRequest?.service_type.replace(/_/g, ' ')} for {editRequest?.user?.first_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-slate-300 text-sm">Status</label>
                <Select 
                  value={editForm.status} 
                  onValueChange={(v) => setEditForm({ ...editForm, status: v })}
                >
                  <SelectTrigger className="input-default">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="quoted">Quoted</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-slate-300 text-sm">Quote Amount (₦)</label>
                <Input
                  type="number"
                  placeholder="Enter quote amount"
                  value={editForm.quote}
                  onChange={(e) => setEditForm({ ...editForm, quote: e.target.value })}
                  className="input-default"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-slate-300 text-sm">Admin Notes</label>
                <Textarea
                  placeholder="Internal notes..."
                  value={editForm.admin_notes}
                  onChange={(e) => setEditForm({ ...editForm, admin_notes: e.target.value })}
                  className="input-default min-h-[80px]"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditRequest(null)} className="btn-secondary">
                Cancel
              </Button>
              <Button onClick={handleSaveRequest} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminRequestsPage;
